import { appConfig, getApiBaseCandidates, isAbsoluteHttpUrl, resolveBackendAssetUrl } from '../config/appConfig';

const API_BASE_URL = appConfig.api.baseUrl;

export type CooperationItem = {
  index: string;
  title: string;
  description: string;
};

export type ProjectCooperationEntry = {
  id: number;
  group_key: string;
  slug: string;
  kind: 'project-cooperation';
  language: 'zh' | 'en' | 'ja';
  available_languages: Array<'zh' | 'en' | 'ja'>;
  badge: string;
  title: string;
  summary: string;
  body: string[];
  cooperation_items: CooperationItem[];
  questionnaire_title: string;
  questionnaire_description: string[];
  qr_hint: string;
  qr_image_url: string | null;
  published_at: string;
  is_pinned: boolean;
  is_default_display: boolean;
};

type CooperationListResponse = {
  results: ProjectCooperationEntry[];
};

type CooperationDetailResponse = {
  item: ProjectCooperationEntry;
  translation_slugs?: Partial<Record<'zh' | 'en' | 'ja', string>>;
};

export class ProjectCooperationApiError extends Error {
  status?: number;
  kind: 'network' | 'http';

  constructor(message: string, kind: 'network' | 'http', status?: number) {
    super(message);
    this.name = 'ProjectCooperationApiError';
    this.kind = kind;
    this.status = status;
  }
}

const normalizeEntry = (item: ProjectCooperationEntry): ProjectCooperationEntry => ({
  ...item,
  qr_image_url: resolveBackendAssetUrl(item.qr_image_url),
});

const buildApiBases = () => {
  const bases = new Set<string>(getApiBaseCandidates());

  if (isAbsoluteHttpUrl(API_BASE_URL)) {
    bases.add(API_BASE_URL);
  }

  return Array.from(bases).map((base) => base.replace(/\/$/, ''));
};

const fetchJson = async <T,>(path: string): Promise<T> => {
  const apiBases = buildApiBases();
  let lastError: unknown = null;

  for (let index = 0; index < apiBases.length; index += 1) {
    try {
      const response = await fetch(`${apiBases[index]}${path}`);
      if (!response.ok) {
        throw new ProjectCooperationApiError(`Request failed: ${response.status}`, 'http', response.status);
      }
      return response.json() as Promise<T>;
    } catch (error) {
      lastError = error;
      if (index === apiBases.length - 1) {
        break;
      }
    }
  }

  if (lastError instanceof ProjectCooperationApiError) {
    throw lastError;
  }

  throw new ProjectCooperationApiError(
    lastError instanceof Error ? lastError.message : 'Backend is unavailable.',
    'network',
  );
};

export const fetchProjectCooperationList = async (lang: 'zh' | 'en' | 'ja'): Promise<ProjectCooperationEntry[]> => {
  const data = await fetchJson<CooperationListResponse>(`/project-cooperations/?lang=${lang}`);
  return data.results.map(normalizeEntry);
};

export const fetchProjectCooperationDetail = async (
  slug: string,
  lang: 'zh' | 'en' | 'ja',
): Promise<ProjectCooperationEntry> => {
  const data = await fetchJson<CooperationDetailResponse>(`/project-cooperations/${slug}/?lang=${lang}`);
  return normalizeEntry(data.item);
};
